import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import SystemSettingsService from 'api/systemSettings/SystemSettingsService';
import Button from 'common/components/button/Button';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import { Reducers } from 'store/types';

import styles from './PlatformSettingsScreen.module.scss';

const KEY_ANDROID = 'MobileAppConfigs:AndroidMinimumAppVersion';
const KEY_IOS = 'MobileAppConfigs:IosMinimumAppVersion';

const PlatformSettingsScreen: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const isPlatformAdmin = UsersService.isPlatformAdmin(userProfile?.roles);

    const [androidMinVersion, setAndroidMinVersion] = useState<string>('');
    const [iosMinVersion, setIosMinVersion] = useState<string>('');

    const loadSettings = async () => {
        try {
            Loading.show();
            const list = await SystemSettingsService.get('MobileAppConfigs');
            const android = list.find(s => s.key === KEY_ANDROID);
            const ios = list.find(s => s.key === KEY_IOS);
            setAndroidMinVersion(android?.value ?? '');
            setIosMinVersion(ios?.value ?? '');
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load system settings', error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        if (isPlatformAdmin) {
            loadSettings();
        }
    }, [isPlatformAdmin]);

    const onSave = async () => {
        try {
            Loading.show();
            await SystemSettingsService.update([
                { key: KEY_ANDROID, value: (androidMinVersion ?? '').trim() },
                { key: KEY_IOS, value: (iosMinVersion ?? '').trim() },
            ]);
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t save system settings', error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    if (!isPlatformAdmin) {
        return null;
    }

    return (
        <CompactPageLayout title={t('platform_settings.title')}>
            <div className={styles.content}>
                <h2 className={styles.sectionTitle}>{t('platform_settings.mobile_section')}</h2>

                <div className={styles.field}>
                    <Label>{t('platform_settings.android_min_version')}</Label>
                    <Input
                        value={androidMinVersion}
                        onChange={e => setAndroidMinVersion(e.currentTarget.value)}
                        placeholder="0.0.0"
                        maxLength={50}
                    />
                    <span className={styles.hint}>{t('platform_settings.version_format_hint')}</span>
                </div>

                <div className={styles.field}>
                    <Label>{t('platform_settings.ios_min_version')}</Label>
                    <Input
                        value={iosMinVersion}
                        onChange={e => setIosMinVersion(e.currentTarget.value)}
                        placeholder="0.0.0"
                        maxLength={50}
                    />
                    <span className={styles.hint}>{t('platform_settings.version_format_hint')}</span>
                </div>

                <div className={styles.buttonContainer}>
                    <Button type="button" text={t('common.save')} onClick={onSave} />
                </div>
            </div>
        </CompactPageLayout>
    );
};

export default PlatformSettingsScreen;
